/** @file applyHostTransform: a node's host yaw offset and part transform on its PoseBuffer row (22.3). */
import { tilePxOf } from '@zilo/pose3d-format/profile/tilePxOf';
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import { quatFromYawPitchRoll } from '@zilo/pose3d-math/quat/quatFromYawPitchRoll';
import { quatMul } from '@zilo/pose3d-math/quat/quatMul';
import { quatRotateVec3 } from '@zilo/pose3d-math/quat/quatRotateVec3';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import { partPointOf } from '@zilo/pose3d-pose/chain/partPointOf';
import { SHAKE_WAVE_DEFAULT } from '@zilo/pose3d-pose/chain/shakeWaveDefault';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';

const own = <T>(rec: Record<string, T> | undefined, key: string): T | undefined =>
  (rec !== undefined && Object.hasOwn(rec, key) ? rec[key] : undefined);
const at = (a: Float64Array, k: number): Vec3 => [a[k] ?? 0, a[k + 1] ?? 0, a[k + 2] ?? 0];
const POS = [L.px, L.py, L.pz] as const;
const ORIGIN = [L.ox, L.oy, L.oz] as const;
const ROT = [L.qx, L.qy, L.qz, L.qw] as const;
type Columns = readonly [number, number, number];
const read = (out: PoseBuffer, o: number, c: Columns): Vec3 => [out[o + c[0]] ?? 0, out[o + c[1]] ?? 0, out[o + c[2]] ?? 0];
const write = (out: PoseBuffer, o: number, c: Columns, v: Vec3): void => {
  out[o + c[0]] = v[0]; out[o + c[1]] = v[1]; out[o + c[2]] = v[2];
};

/** `yaw(q, p) + move`: the accumulated yaw turn applied to a point. */
function turned(q: Quat, p: Vec3, move: Vec3): Vec3 {
  const r = quatRotateVec3(q, p);
  return [r[0] + move[0], r[1] + move[1], r[2] + move[2]];
}

/**
 * Node `i`'s host step on its `out` row (position, rotation, bone origin already through mirror, tilt and facing), in
 * place, in parent-first order, before the read-back. (1) 22.3 (d) `nodeYawDeg[node] = a` (not 0): the node and its
 * subtree turn by `a` about the vertical through the node's bone origin (as its ancestors' turns leave it), rotations
 * `q <- Rz(a) * q`; the bone origin itself stays. A turn about the vertical commutes with the facing, so turning the
 * faced row equals `Rz(a)` in body space after the tilt; under `mirrorX` the world sense stays `a`. Turns sum down the
 * tree in `rig.workHostYaw` / `workHostMove`. (2) 22.3 (a, b) `partTransform[node]` (`partPointOf`, once per point, no
 * inheritance): the node position always; the bone origin when `ownOrigin` (an originOffset or key translation); a
 * root's bone origin without one stays (22.3 e); any other is the effective parent's already-moved position. The shake
 * wave is `params.shakeWave` (default `SHAKE_WAVE_DEFAULT`). An exempt node (no record) keeps its points.
 */
export function applyHostTransform(rig: Rig, i: number, params: PoseParams, ownOrigin: boolean, out: PoseBuffer): void {
  const p = rig.workParent[i] ?? -1;
  const name = rig.names[i] ?? '';
  const o = i * L.stride;
  const above = p < 0 ? 0 : (rig.workHostYaw[p] ?? 0);
  let move: Vec3 = p < 0 ? [0, 0, 0] : at(rig.workHostMove, p * 3);
  let on = p < 0 ? 0 : (rig.workHostOn[p] ?? 0);
  const a = own(params.nodeYawDeg, name) ?? 0;
  if (a !== 0) {
    // Pivot c = the bone origin as the ancestors' turns leave it; move <- turn(a, move_p - c) + c.
    const origin = read(out, o, ORIGIN);
    const c = on !== 0 ? turned(quatFromYawPitchRoll(above, 0, 0), origin, move) : origin;
    move = turned(quatFromYawPitchRoll(a, 0, 0), [move[0] - c[0], move[1] - c[1], move[2] - c[2]], c);
    on = 1;
  }
  const yaw = above + a;
  rig.workHostOn[i] = on;
  rig.workHostYaw[i] = yaw;
  rig.workHostMove.set(move, i * 3);
  if (on !== 0) {
    const q = quatFromYawPitchRoll(yaw, 0, 0);
    write(out, o, POS, turned(q, read(out, o, POS), move));
    write(out, o, ORIGIN, turned(q, read(out, o, ORIGIN), move));
    const r = quatMul(q, [out[o + ROT[0]] ?? 0, out[o + ROT[1]] ?? 0, out[o + ROT[2]] ?? 0, out[o + ROT[3]] ?? 1]);
    ROT.forEach((col, k) => { out[o + col] = r[k] ?? 0; });
  }
  const part = own(params.partTransform, name);
  const clock = params.shakeClock ?? 0;
  const wave = params.shakeWave ?? SHAKE_WAVE_DEFAULT;
  const ppu = tilePxOf(params.profile)[0];
  if (part !== undefined) write(out, o, POS, partPointOf(read(out, o, POS), part, clock, ppu, wave));
  if (ownOrigin) {
    if (part !== undefined) write(out, o, ORIGIN, partPointOf(read(out, o, ORIGIN), part, clock, ppu, wave));
  } else if (p >= 0 && own(params.partTransform, rig.names[p] ?? '') !== undefined) {
    write(out, o, ORIGIN, read(out, p * L.stride, POS));
  }
}
